<template>
  <div class="pruebas-wrap">
    <!-- El shadow root cuelga de acá; su contenido lo maneja este componente
         con DOM plano (ver shadowMount.js). -->
    <div ref="host" class="pruebas-host"></div>
    <p v-if="error" class="pruebas-error">
      No pudimos cargar el detalle de las pruebas. Inicia sesión y
      descárgalas: son gratis.
    </p>
  </div>
</template>

<script>
// El cuerpo de /pruebas-de-especialidad/ dentro del chatbot demo: las
// cifras, "elige tu especialidad" con sus tres viñetas animadas, un bloque por
// especialidad con lo que mide cada prueba —cada eje con su mini-escena
// animada— y cómo funciona.
//
// Mismo marcado y mismas hojas que la página (el endpoint renderiza el parcial
// marketing/_pruebas_secciones.html), montados en un shadow root para que el
// CSS del sitio no se cruce con el del SPA. Los dos animadores de abajo son
// port del script de la página: dos relojes, un solo contenido.
import informationService from "@/services/informationService";
import { montarEnSombra } from "./shadowMount";

// Lo que cambia respecto de la página. Va después de las hojas (ver
// shadowMount.js).
const AJUSTES =
  // Los márgenes de sección del sitio están pensados para una página entera;
  // en el chat el bloque ya viene enmarcado.
  ".section{padding-block:1.75rem}" +
  ".shell{max-width:none;padding-inline:1.25rem}" +
  // Fuera: las tres demos en <iframe> (documentos aparte que el chat no
  // puede enmarcar), sus botones de escena, que solo hablan con ellas, y los
  // "inicia sesión" del bloque, porque el menú del chat ya trae el suyo.
  ".demo,.pasos,.btn-row{display:none!important}" +
  // Las tres fichas de "elige tu especialidad", del mismo ancho. La hoja usa
  // `repeat(3,1fr)`, y `1fr` es `minmax(auto,1fr)`: la rejilla de 12 símbolos
  // de la viñeta de Psicología impone su ancho mínimo y abre su columna a costa
  // de las otras dos. En la página no se nota porque el hueco es de 1.180 px;
  // en el chat sí, y la regla que la apila bajo 900 px no ayuda —dentro de un
  // shadow root mide la ventana, no la columna (ver shadowMount.js)—. Con
  // `minmax(0,1fr)` cada columna ignora su contenido y `.vig` recorta el resto.
  ".elige__grid{grid-template-columns:repeat(3,minmax(0,1fr))}" +
  // Y cuando el hueco del chat es angosto se apilan, como en la página bajo
  // 900 px: tres fichas en una fila de teléfono no se leen. La consulta va por
  // `@container` y no por `@media` por lo mismo de arriba —la media query mide
  // la ventana—, así que el host se declara contenedor y la rejilla pregunta
  // por el ancho que de verdad tiene.
  ":host{container-type:inline-size}" +
  "@container (max-width:900px){.elige__grid{grid-template-columns:minmax(0,1fr)}}";

export default {
  name: "PruebasSecciones",
  data() {
    return { error: false };
  },
  mounted() {
    informationService
      .getPruebasSecciones()
      .then((payload) => this.montar(payload))
      .catch(() => {
        this.error = true;
      });
  },
  beforeDestroy() {
    this.parar();
  },
  methods: {
    montar(payload) {
      const montado = montarEnSombra(this.$refs.host, payload, {
        ajustes: AJUSTES,
      });
      if (!montado) {
        this.error = true;
        return;
      }
      this.raiz = montado.raiz;
      this.timers = {};
      this.observadores = [];

      this.raiz.addEventListener("click", this.anclas);
      window.addEventListener("resize", this.encajar);
      this.encajar();
      this.despues("encajar", this.encajar, 300);

      if (!window.IntersectionObserver) {
        this.vinetas();
        this.escenas().forEach((m) => {
          m._on = true;
          this.escena(m);
        });
        return;
      }
      // Las viñetas de portada corren mientras su rejilla está a la vista.
      const rejilla = this.q(".elige__grid");
      if (rejilla) {
        this.observar(rejilla, 0.15, (visible) =>
          visible ? this.vinetas() : this.limpiar("vig")
        );
      }
      // Cada mini-escena, mientras ella misma está a la vista.
      this.escenas().forEach((m) =>
        this.observar(m, 0.3, (visible) => {
          if (visible && !m._on) {
            m._on = true;
            this.escena(m);
          } else if (!visible) {
            m._on = false;
            this.limpiar(m.dataset.id);
          }
        })
      );
    },

    parar() {
      Object.keys(this.timers || {}).forEach(this.limpiar);
      (this.observadores || []).forEach((o) => o.disconnect());
      window.removeEventListener("resize", this.encajar);
      if (this.raiz) this.raiz.removeEventListener("click", this.anclas);
    },

    // --- utilidades -------------------------------------------------------
    q(sel) {
      return this.raiz.querySelector(sel);
    },
    qa(sel, desde) {
      return Array.from((desde || this.raiz).querySelectorAll(sel));
    },
    despues(clave, fn, ms) {
      if (!this.timers[clave]) this.timers[clave] = [];
      this.timers[clave].push(setTimeout(fn, ms));
    },
    limpiar(clave) {
      (this.timers[clave] || []).forEach((t) => {
        clearTimeout(t);
        clearInterval(t);
      });
      this.timers[clave] = [];
    },
    observar(el, threshold, alCambiar) {
      const o = new IntersectionObserver(
        (es) => es.forEach((e) => alCambiar(e.isIntersecting)),
        { threshold }
      );
      o.observe(el);
      this.observadores.push(o);
    },
    // Las fichas de "elige tu especialidad" enlazan a `#psicopedagogia` y
    // compañía. Dentro del SPA ese hash lo toma el router de Vue y manda al
    // visitante a otra pantalla, así que el salto se hace a mano, dentro del
    // shadow root.
    anclas(e) {
      const a = e.target.closest && e.target.closest('a[href^="#"]');
      if (!a) return;
      e.preventDefault();
      const destino = this.raiz.getElementById(a.getAttribute("href").slice(1));
      if (destino) destino.scrollIntoView({ behavior: "smooth", block: "start" });
    },

    // --- viñetas de portada ------------------------------------------------
    vinetas() {
      this.limpiar("vig");
      const later = (fn, ms) => this.despues("vig", fn, ms);

      const pp = () => {
        const ws = this.qa(".vig--pp .vig__read span");
        const pcm = this.q("[data-vpcm]");
        const rub = this.qa(".vig--pp .vig__rub span");
        ws.forEach((w) => (w.className = ""));
        rub.forEach((r) => r.classList.remove("on"));
        pcm.textContent = "0";
        ws.forEach((w, i) =>
          later(() => {
            w.classList.add(i === 4 ? "err" : "on");
            pcm.textContent = Math.round((i + 1) * 8.2);
          }, 350 + i * 330)
        );
        rub.forEach((r, i) =>
          later(
            () => r.classList.add("on"),
            350 + ws.length * 330 + 300 + i * 300
          )
        );
        later(pp, 350 + ws.length * 330 + 2600);
      };

      const fo = () => {
        const ty = this.q("[data-vtype]");
        const sil = this.qa(".vig--fo .vig__sil span");
        const sb = this.q(".vig--fo .vig__sil b");
        const tk = this.q(".vig--fo .vig__tick");
        ty.textContent = "";
        sil.forEach((s) => s.classList.remove("on"));
        sb.classList.remove("on");
        tk.classList.remove("on");
        const word = ty.getAttribute("data-vtype");
        for (let i = 0; i < word.length; i++) {
          later(() => (ty.textContent = word.slice(0, i + 1)), 700 + i * 220);
        }
        const t = 700 + word.length * 220 + 400;
        sil.forEach((s, i) => later(() => s.classList.add("on"), t + i * 350));
        later(() => sb.classList.add("on"), t + 800);
        later(() => tk.classList.add("on"), t + 1300);
        later(fo, t + 4000);
      };

      const ps = () => {
        const cells = this.qa(".vig--ps .vig__grid span.t");
        const ck = this.q("[data-vclock]");
        const ok = this.q(".vig--ps .vig__opts i.ok");
        cells.forEach((c) => c.classList.remove("on"));
        ok.classList.remove("on");
        const t0 = Date.now();
        const iv = setInterval(() => {
          ck.textContent = Math.floor((Date.now() - t0) / 1000);
        }, 250);
        this.timers.vig.push(iv);
        cells.forEach((c, i) => later(() => c.classList.add("on"), 500 + i * 520));
        const t = 500 + cells.length * 520;
        later(() => {
          clearInterval(iv);
          ok.classList.add("on");
        }, t + 400);
        later(ps, t + 3200);
      };

      pp();
      fo();
      ps();
    },

    // --- mini-escenas de "lo que mide" -------------------------------------
    escenas() {
      return this.qa(".mv");
    },
    // Cada escena se dibuja a 560 px y se escala al ancho de su ficha.
    encajar() {
      if (!this.raiz) return;
      this.escenas().forEach((m) => {
        m.querySelector(".mv__st").style.transform = `scale(${
          m.clientWidth / 560
        })`;
      });
    },
    escena(m) {
      const id = m.dataset.id;
      const fn = this.guiones()[m.dataset.kind];
      if (!fn) return;
      this.limpiar(id);
      const dura = fn(m, (f, ms) => this.despues(id, f, ms));
      this.despues(id, () => m._on && this.escena(m), dura);
    },
    guiones() {
      const type = (later, el, w, t0) => {
        for (let i = 0; i < w.length; i++) {
          later(() => (el.textContent = w.slice(0, i + 1)), t0 + i * 140);
        }
        return t0 + w.length * 140;
      };
      return {
        puzzle: (m, later) => {
          const pre = m.dataset.pre;
          const ans = m.querySelector(".tag");
          const fig = m.querySelector(".fig img");
          const flies = Array.from(m.querySelectorAll(".fly"));
          if (!m._t0) m._t0 = flies.map((f) => [f.style.left, f.style.top]);
          const TG = JSON.parse(m.dataset.tg);
          flies.forEach((f, i) => {
            f.classList.remove("on");
            f.style.left = m._t0[i][0];
            f.style.top = m._t0[i][1];
            f.style.width = "44px";
            f.style.height = "44px";
            f.style.transform = "";
          });
          m.querySelectorAll(".pb").forEach((p) => p.classList.remove("okp"));
          ans.classList.remove("on");
          fig.classList.remove("on");
          TG.forEach((t, i) =>
            later(() => {
              const e = flies[i];
              e.classList.add("on");
              e.style.left = `${t[1]}px`;
              e.style.top = `${t[2]}px`;
              e.style.width = `${t[3]}px`;
              e.style.height = `${t[4]}px`;
              e.style.transform = t[5];
              m.querySelector(`#${pre}pb${t[0]}`).classList.add("okp");
            }, 700 + i * 700)
          );
          later(() => {
            fig.classList.add("on");
            ans.classList.add("on");
          }, 700 + TG.length * 700 + 300);
          return 700 + TG.length * 700 + 3200;
        },
        opts: (m, later) => {
          const o = m.querySelectorAll(".opt");
          o.forEach((e) => {
            e.classList.remove("ok");
            e.querySelector(".chk").classList.remove("on");
          });
          const k = +m.dataset.ok;
          later(() => {
            o[k].classList.add("ok");
            o[k].querySelector(".chk").classList.add("on");
          }, 1200);
          return 4200;
        },
        chart: (m, later) => {
          const b = m.querySelectorAll(".bar");
          const a = m.querySelector(".tag");
          b.forEach((e) => {
            e.style.height = "0";
            e.classList.remove("on");
          });
          a.classList.remove("on");
          b.forEach((e, i) =>
            later(() => {
              e.style.height = getComputedStyle(e).getPropertyValue("--h");
              e.classList.add("on");
            }, 500 + i * 350)
          );
          later(() => a.classList.add("on"), 500 + b.length * 350 + 500);
          return 500 + b.length * 350 + 3500;
        },
        ejes: (m, later) => {
          const e = m.querySelectorAll(".eje");
          e.forEach((x) => x.classList.remove("on"));
          e.forEach((x, i) => later(() => x.classList.add("on"), 500 + i * 450));
          return 500 + e.length * 450 + 3000;
        },
        tabla: (m, later) => {
          const r = m.querySelectorAll("tr");
          const a = m.querySelector(".tag");
          r.forEach((x) => x.classList.remove("on"));
          a.classList.remove("on");
          const rows = Array.from(r).slice(1);
          rows.forEach((x, i) => later(() => x.classList.add("on"), 500 + i * 700));
          later(() => a.classList.add("on"), 500 + rows.length * 700 + 300);
          return 500 + rows.length * 700 + 3300;
        },
        typed: (m, later) => {
          const t = m.querySelectorAll(".typed");
          const a = m.querySelector(".tag");
          t.forEach((x) => (x.textContent = ""));
          if (a) a.classList.remove("on");
          let t0 = 600;
          t.forEach((x) => {
            t0 = type(later, x, x.dataset.w, t0) + 500;
          });
          if (a) later(() => a.classList.add("on"), t0);
          return t0 + 3000;
        },
        pic: (m, later) => {
          const n = m.querySelectorAll(".num,.chk");
          const p = m.querySelectorAll(".pill");
          n.forEach((x) => x.classList.remove("on"));
          p.forEach((x) => x.classList.remove("on"));
          n.forEach((x, i) =>
            later(() => {
              x.classList.add("on");
              if (p[i]) p[i].classList.add("on");
            }, 700 + i * 600)
          );
          return 700 + n.length * 600 + 3000;
        },
        pauta: (m, later) => {
          const r = m.querySelectorAll(".ind");
          r.forEach((x) =>
            x.querySelectorAll(".lpn span").forEach((s) => s.classList.remove("on"))
          );
          r.forEach((x, i) =>
            later(
              () => x.querySelectorAll(".lpn span")[+x.dataset.v].classList.add("on"),
              600 + i * 650
            )
          );
          return 600 + r.length * 650 + 3000;
        },
      };
    },
  },
};
</script>

<style scoped>
.pruebas-wrap {
  width: 100%;
}

.pruebas-host {
  display: block;
  border-radius: 18px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 24px 46px rgba(57, 62, 70, 0.15);
}

.pruebas-error {
  margin: 0;
  padding: 1.2rem 1.4rem;
  color: #5c636d;
  text-align: left;
}
</style>
